import { useState, useEffect } from 'react';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import Skeleton from '@mui/material/Skeleton';
import StatusCharts from '@IssueDashboards/StatusCharts';
import FieldCharts from '@IssueDashboards/FieldCharts';
import AnalyticsCharts from '@IssueDashboards/AnalyticsCharts';
import UserTicketBracketCharts from '@IssueDashboards/UserTicketBracketCharts';
import CreateCharts from '@IssueDashboards/CreateCharts';
import TicketService from '@Helpers/URLHelpers/KaiziqResolve/TicketService';
import KaiziqResolveConnectionExpandService from '@Helpers/URLHelpers/KaiziqResolve/views/KaiziqResolveConnectionExpandService';
import KaiziqResolveConnectionAnalyticsService from '@Helpers/URLHelpers/KaiziqResolve/views/KaiziqResolveConnectionAnalyticsService';
import TicketUserEmailCountBracketService from '@Helpers/URLHelpers/KaiziqResolve/views/TicketUserEmailCountBracketService';
import TicketUserEmailCountViewService from '@Helpers/URLHelpers/KaiziqResolve/views/TicketUserEmailCountViewService';
import ErrorHandler from '@Root/ErrorHandler';
import { MdAssessment as Assessment } from 'react-icons/md';
import { MdPieChart as PieChartIcon } from 'react-icons/md';
import { MdBarChart as BarChartIcon } from 'react-icons/md';
import { MdPeople as PeopleIcon } from 'react-icons/md';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_ComStyles.scss';
import './FormReportsPage.scss';

export default function FormReportsPage(props) {

    const [kaiziqResolveConnection, setKaiziqResolveConnection] = useState({});
    const [kaiziqResolveConnectionStats, setKaiziqResolveConnectionStats] = useState({});
    const [kaiziqResolveConnectionAnalytics, setKaiziqResolveConnectionAnalytics] = useState({});
    const [formFeedbackAnalysis, setFormFeedbackAnalysis] = useState();
    const [ticketUserEmailCountBracket, setTicketUserEmailCountBracket] = useState([]);
    const [ticketStatusList, setTicketStatusList] = useState([]);
    const [topReporter, setTopReporter] = useState({});
    const [loading, setLoading] = useState(false);
    const { currentProject } = useReduxProject();

    const kaiziqResolveConnectionExpandService = new KaiziqResolveConnectionExpandService();
    const kaiziqResolveConnectionAnalyticsService = new KaiziqResolveConnectionAnalyticsService();
    const ticketUserEmailCountBracketService = new TicketUserEmailCountBracketService();
    const ticketUserEmailCountViewService = new TicketUserEmailCountViewService();
    const errorHandler = new ErrorHandler();
    const ticketService = new TicketService();
    const cn = 'resolve__formReportsPage';

    useEffect(() => {
        setKaiziqResolveConnection(props.kaiziqResolveConnection);
    }, [props.kaiziqResolveConnection]);

    // Load once per form. Depending on the whole props object re-fetched everything (and
    // flashed the skeletons) every time the parent re-rendered.
    useEffect(() => {
        if (!props.kaiziqResolveConnection?.id) return;
        renderData(true);
    }, [props.kaiziqResolveConnection?.id]);

    // Skeletons are only for the first load of a form; later reloads swap the numbers in place.
    const renderData = async (showLoader = false) => {
        if (showLoader === true) setLoading(true);
        try {
            let statsObj = await kaiziqResolveConnectionExpandService.getById(props.kaiziqResolveConnection.id);
            setKaiziqResolveConnectionStats(JSON.parse(statsObj.body));

            let analyticsObj = await kaiziqResolveConnectionAnalyticsService.getByFormId(props.kaiziqResolveConnection?.id);
            setKaiziqResolveConnectionAnalytics(JSON.parse(analyticsObj.body));

            let bracketObj = await ticketUserEmailCountBracketService.getListByForm(props.kaiziqResolveConnection?.id);
            setTicketUserEmailCountBracket(JSON.parse(bracketObj.body));

            let statusObj = await ticketService.getAnalysisByForm(props.kaiziqResolveConnection?.id);
            setTicketStatusList(JSON.parse(statusObj.body).statusList);

            let topObj = await ticketUserEmailCountViewService.getTopByForm(props.kaiziqResolveConnection?.id);
            setTopReporter(JSON.parse(topObj.body));

            let feedbackObj = await ticketService.getFeedbackAnalysisByForm(props.kaiziqResolveConnection.id);
            setFormFeedbackAnalysis(JSON.parse(feedbackObj.body));
        } catch (err) {
            errorHandler.handleError(err);
        }
        setLoading(false);
    };

    const sharedProps = {
        kaiziqResolveConnection,
        kaiziqResolveConnectionStats,
        ticketStatusList,
        ticketUserEmailCountBracket,
        kaiziqResolveConnectionAnalytics,
        topReporter,
        project: currentProject,
        reloadData: () => renderData(),
        createTimeStampList: props.createTimeStampList,
    };

    const hasData = ticketStatusList?.length > 0 || (formFeedbackAnalysis && Object.keys(formFeedbackAnalysis).length > 0);

    if (loading) {
        return (
            <div className={`${cn}__loader`}>
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '14px', mb: 2 }} />
                <div className={`${cn}__loader__row3`}>
                    {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} variant="rectangular" height={160} sx={{ borderRadius: '14px' }} />
                    ))}
                </div>
                <div className={`${cn}__loader__row2`}>
                    <Skeleton variant="rectangular" height={340} sx={{ borderRadius: '14px', flex: 3 }} />
                    <Skeleton variant="rectangular" height={340} sx={{ borderRadius: '14px', flex: 2 }} />
                </div>
                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: '14px' }} />
            </div>
        );
    }

    return (
        <div className={cn}>
            {/* ── Header ────────────────────────────────────────────── */}
            <div className={`${cn}__header`}>
                <div className={`${cn}__header__left`}>
                    <div className={`${cn}__header__iconWrap`}>
                        <Assessment className={`${cn}__header__icon`} />
                    </div>
                    <div className={`${cn}__header__text`}>
                        <h1 className={`${cn}__header__title`}>
                            {props.kaiziqResolveConnection?.title || 'Form Analytics'}
                        </h1>
                        <p className={`${cn}__header__subtitle`}>Analytics & Reports Dashboard</p>
                    </div>
                </div>
                <div className={`${cn}__header__right`}>
                    <div className={`${cn}__header__stat`}>
                        <span className={`${cn}__header__stat__num`}>
                            {kaiziqResolveConnectionStats?.totalTickets ?? '—'}
                        </span>
                        <span className={`${cn}__header__stat__label`}>Total</span>
                    </div>
                    <div className={`${cn}__header__divider`} />
                    <div className={`${cn}__header__stat`}>
                        <span className={`${cn}__header__stat__num active`}>
                            {kaiziqResolveConnectionAnalytics?.activeTickets ?? '—'}
                        </span>
                        <span className={`${cn}__header__stat__label`}>Active</span>
                    </div>
                    <div className={`${cn}__header__divider`} />
                    <div className={`${cn}__header__stat`}>
                        <span className={`${cn}__header__stat__num resolved`}>
                            {kaiziqResolveConnectionAnalytics?.resolvedTickets ?? '—'}
                        </span>
                        <span className={`${cn}__header__stat__label`}>Resolved</span>
                    </div>
                </div>
            </div>

            {hasData ? (
                <>
                    {/* ── Analytics Overview (stat cards + status breakdown) ── */}
                    <section className={`${cn}__section`}>
                        <AnalyticsCharts {...sharedProps} />
                    </section>

                    {/* ── Insights Row: pie chart + reporter cards ────────── */}
                    <div className={`${cn}__insightsRow`}>
                        {/* Status Distribution */}
                        <div className={`${cn}__insightsRow__chart`}>
                            <div className={`${cn}__card`}>
                                <div className={`${cn}__card__header`}>
                                    <PieChartIcon className={`${cn}__card__header__icon`} />
                                    <span className={`${cn}__card__header__title`}>Status Distribution</span>
                                </div>
                                <StatusCharts {...sharedProps} />
                            </div>
                        </div>

                        {/* Reporter Insights */}
                        <div className={`${cn}__insightsRow__reporters`}>
                            <div className={`${cn}__card`}>
                                <div className={`${cn}__card__header`}>
                                    <PeopleIcon className={`${cn}__card__header__icon`} />
                                    <span className={`${cn}__card__header__title`}>Reporter Insights</span>
                                </div>
                                <UserTicketBracketCharts {...sharedProps} />
                            </div>
                        </div>
                    </div>

                    {/* ── Creation Timeline ───────────────────────────────── */}
                    <div className={`${cn}__card`}>
                        <div className={`${cn}__card__header`}>
                            <BarChartIcon className={`${cn}__card__header__icon`} />
                            <span className={`${cn}__card__header__title`}>Ticket Creation Timeline</span>
                        </div>
                        <CreateCharts {...sharedProps} />
                    </div>

                    {/* ── Field Responses ─────────────────────────────────── */}
                    {formFeedbackAnalysis && Object.keys(formFeedbackAnalysis).length > 0 && (
                        <section className={`${cn}__fields`}>
                            <div className={`${cn}__fields__header`}>
                                <PieChartIcon className={`${cn}__fields__header__icon`} />
                                <h2 className={`${cn}__fields__header__title`}>Field Responses</h2>
                                <span className={`${cn}__fields__header__badge`}>
                                    {Object.keys(formFeedbackAnalysis).length} fields
                                </span>
                            </div>
                            <div className={`${cn}__fields__grid`}>
                                {Object.keys(formFeedbackAnalysis).map((fieldId, index) => (
                                    <FieldCharts
                                        key={index}
                                        formFeedbackAnalysis={formFeedbackAnalysis}
                                        fieldId={fieldId}
                                    />
                                ))}
                            </div>
                        </section>
                    )}
                </>
            ) : (
                /* ── Empty State ──────────────────────────────────────── */
                <div className={`${cn}__empty`}>
                    <Assessment className={`${cn}__empty__icon`} />
                    <h3 className={`${cn}__empty__title`}>No Data Yet</h3>
                    <p className={`${cn}__empty__desc`}>
                        Analytics will appear here once your form starts receiving submissions.
                    </p>
                </div>
            )}
        </div>
    );
}
